// order manager 上报
import { commonRequest } from '@/common/utils/request'
import { getIsOrderManager, getLoginAccountId, getLoginShopid, getLoginShopName, getInWindowsApp, getRegion, getRegionByCookie, getWebId, safeParse } from '@RootCommon/utils'
import { AreaEnum, MArea } from '@sailor/i18n-web'
import { getHornConfig } from '@RootCommon/utils/horn'
import { LS_PRINT } from '../constants'

const isOrderManager = getIsOrderManager()
const isInWindowsApp = getInWindowsApp()
const isGCC = [...MArea.getRegionsByArea(AreaEnum.GCC)].includes(getRegionByCookie())

const DEVICE_TYPE = {
  WINDOWS: 2, // om windows
  WEB: 1, // om web
  PORTAL: 4,
}

export const getDeviceType = () => {
  if (isOrderManager) {
    return isInWindowsApp ? DEVICE_TYPE.WINDOWS : DEVICE_TYPE.WEB
  }
  if (isGCC) {
    return DEVICE_TYPE.PORTAL
  }
}

export const getCommonParams = () => ({
  shopId: getLoginShopid(),
  shopName: getLoginShopName(),
  deviceUuid: getWebId(),
  deviceSn: getWebId(),
  reportTime: Date.now(),
  isWebVisible: document.visibilityState === 'visible' ? 1 : 0,
})

async function getRetryConfig () {
  const config = await getHornConfig()
  return config.retryConfig
}

/**
 * 设备状态心跳上报
 * @param deviceType 设备类型
 * @param heartReportType 心跳上报类型 1: 轮询心跳上报 2: 探活心跳上报
 */
export async function deviceHeartBeat (
  deviceType: number,
  heartReportType: 1 | 2,
  payload: {
    networkType: string
    networkDelay: number
    networkLatencyTp99: number | null
    cpuUsage?: number
    memoryUsage?: number
    lastShutdownTime?: number
    appVersion?: string
  },
) {
  const commonParams = getCommonParams()
  if (commonParams.shopId <= 0) {
    throw new Error(`shopId无效: ${commonParams.shopId}`)
  }
  const retryConfig = await getRetryConfig()
  return commonRequest.post(
    '/api/operate/device/report',
    {
      deviceType,
      heartReportType,
      ...commonParams,
      ...payload,
    },
    { ...retryConfig },
  ) as Promise<{
    reportGapTime: number
  }>
}

// 设备心跳统计数据上报
export async function deviceHeartBeatDataReport (
  deviceType: number,
  heartReportInfo: {
    periodCost: number // 上报周期
    total: number // 心跳上报次数
    failed: number // 失败次数
    tp50: number // 前50%耗时
    tp90: number // 前90%耗时
    tp99: number // 前99%耗时
  },
) {
  const commonParams = getCommonParams()
  if (commonParams.shopId <= 0) {
    throw new Error(`shopId无效: ${commonParams.shopId}`)
  }
  const retryConfig = await getRetryConfig()
  return commonRequest.post(
    '/api/operate/device/heartbeatDataReport',
    {
      deviceType,
      ...commonParams,
      heartReportInfo: JSON.stringify(heartReportInfo),
    },
    { ...retryConfig },
  ) as Promise<{
    reportGapTime: number
  }>
}

// 网络上报
export async function networkReport (
  deviceType: number,
  networkCondition: {
    netStatus: number
    trigger: string
    urlTime: Array<{
      time: number
      httpCode: number
      url: string
    }>
  },
) {
  const commonParams = getCommonParams()
  if (commonParams.shopId <= 0) {
    throw new Error(`shopId无效: ${commonParams.shopId}`)
  }
  const retryConfig = await getRetryConfig()
  return commonRequest.post(
    '/api/operate/device/networkReport',
    {
      deviceType,
      ...commonParams,
      networkCondition: JSON.stringify(networkCondition),
    },
    { ...retryConfig },
  ) as Promise<{
    reportGapTime: number
  }>
}

export const DEVICE_REPORT_TYPE = {
  PRINTER: 3,
  NO_SOUND_PERMISSION: 4,
}

/**
 * 设备相关上报 deviceType 3: 打印上报 4: 无声音权限上报
 * @param deviceType 上报类型
 * @param extension 扩展参数, 用于打印机配置上报时需要传入打印配置等信息
 */
export async function deviceReport (deviceType: number, extension?: { [key: string]: any }) {
  const shopId = getLoginShopid()
  if (shopId <= 0) {
    throw new Error(`shopId无效: ${shopId}`)
  }
  const retryConfig = await getRetryConfig()
  const uuid = getWebId()
  return commonRequest.post(
    '/api/operate/device/deviceDataReport',
    {
      shopId,
      userId: deviceType === DEVICE_REPORT_TYPE.PRINTER ? getLoginAccountId() : undefined,
      deviceUuid: uuid,
      deviceType,
      extension: JSON.stringify(extension),
    },
    { ...retryConfig },
  )
}

/**
 * 打印机检测状态上报
 * @param connectionStatus 打印机连接状态：true-已连接 false-已断开
 */
export async function reportPrinterDetectionStatus (connectionStatus: boolean) {
  const shopId = getLoginShopid()
  if (shopId <= 0) {
    throw new Error(`shopId无效: ${shopId}`)
  }
  const getPrinterName = async () => {
    const storeSettings = await window.electronAPI?.store.get(LS_PRINT)
    const settingsInStorage = localStorage.getItem(LS_PRINT)
    const printer = storeSettings?.printer || safeParse(settingsInStorage || '{}')?.printer || 'Unknown'
    return printer
  }

  const uuid = getWebId()
  return commonRequest.post(
    '/api/operate/device/reportPrinterDetection',
    {
      shopId,
      deviceUuid: uuid,
      deviceType: 2, // 设备类型 (2=OM_CLIENT, 5=PARTNER_ANDROID, 6=PARTNER_IOS)
      printerId: await getPrinterName(),
      printerType: 2, // 打印机类型 (1=蓝牙, 2=有线)
      connectionStatus: connectionStatus ? '1' : '2', // 连接状态 (1=已连接, 2=已断开)
      shopRegion: getRegion(),
      region: getRegion(),
      deviceSn: getWebId(),
      reportTime: Date.now(),
    },
    { needRetry: true },
  )
}

/**
 * 设备事件类型枚举
 */
export enum DeviceEventType {
  SYSTEM_SLEEP = 1, // 电脑休眠
  NETWORK_DISCONNECT = 2, // 断网
}

/**
 * 设备事件上报
 * @param eventType 事件类型: 1-电脑休眠, 2-断网
 * @param startTime 事件开始时间
 * @param endTime 事件结束时间
 */
export async function reportDeviceEvents (
  eventType: DeviceEventType,
  startTime: number,
  endTime: number,
) {
  const shopId = getLoginShopid()
  if (shopId <= 0) {
    throw new Error(`shopId无效: ${shopId}`)
  }
  const commonParams = getCommonParams()
  const retryConfig = await getRetryConfig()
  return commonRequest.post(
    '/api/operate/device/events/report',
    {
      deviceType: getDeviceType(),
      ...commonParams,
      event: {
        eventType,
        startTime,
        endTime,
      },
    },
    { ...retryConfig },
  )
}
